import type { User } from "firebase/auth";
import { mountCharacterIdlePreview } from "@/character/character-preview";
import {
  mountCharacterSpritesheetTab,
  type CharacterSpritesheetTabHandle,
} from "@/character/character-spritesheet-tab";
import { mountItemHotbarSettingsPanel } from "@/character/item-hotbar-settings-panel";
import { mountStatAllocationPanel } from "@/character/stat-allocation-panel";
import {
  EQUIPMENT_ITEMS,
  EQUIPMENT_SLOT_LABEL_TH,
  EQUIPMENT_SLOT_ORDER,
  equipItem,
  equipmentStatBonus,
  inventoryForSlot,
  loadEquipmentState,
  saveEquipmentState,
  unequipSlot,
  type EquipmentSlotId,
  type EquipmentState,
} from "@/character/equipment/storage";
import type { CharacterProfile } from "@/character/storage";
import {
  progressionXpDisplay,
  type CharacterProgression,
} from "@/game/model/character-progression";
import { mergeStatBonuses, playerRatings } from "@/game/model/character-stats";
import type { HomeEconomy } from "@/home/economy";
import { HomeEvents } from "@/home/home-events";
import { mountPetPanel, type PetPanelHandle } from "@/pet/mount-pet-panel";
import { mountShadowPanel, type ShadowPanelHandle } from "@/shadow/mount-shadow-panel";
import { shadowEquippedStatBonus } from "@/shadow/shadow-stat-bonus";
import type { ShadowDefinition } from "@/shadow/types";

export type CharacterSheetTabId = "equip" | "stats" | "auto" | "shadow" | "pet" | "sprite";

export type CharacterSheetModalOptions = {
  user: User;
  userId: string;
  profile: CharacterProfile;
  getProgression: () => CharacterProgression;
  getEconomy: () => HomeEconomy;
  onProgressionUpdate: (next: CharacterProgression) => void;
  onEquipmentChange: (state: EquipmentState, bonus: ReturnType<typeof equipmentStatBonus>) => void;
  onEconomySettingsApplied?: (economy: HomeEconomy) => void;
  onEconomyUpdated?: (economy: HomeEconomy) => void;
  getEquippedShadow: () => ShadowDefinition | null;
  getPendingShadowApCardShadowId?: () => string | null;
  onPendingShadowApCardConsumed?: () => void;
};

function formatNum(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 10_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString("en-US");
}

function escapeHtml(text: string): string {
  return text
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

export type CharacterSheetModalHandle = {
  open: () => void;
  openShadowTab: () => void;
  openSpriteTab: () => void;
  close: () => void;
  teardown: () => void;
  getEquipmentState: () => EquipmentState;
};

export function mountCharacterSheetModal(
  shell: HTMLElement,
  options: CharacterSheetModalOptions,
): CharacterSheetModalHandle {
  let equipment = loadEquipmentState(options.userId);
  let activeTab: CharacterSheetTabId = "equip";
  let shadowPanel: ShadowPanelHandle | undefined;
  let petPanel: PetPanelHandle | undefined;
  let spritesheetTab: CharacterSpritesheetTabHandle | undefined;
  let selectedSlot: EquipmentSlotId | null = null;
  let stopPreview: (() => void) | undefined;
  let stopStatPanel: (() => void) | undefined;
  let stopAutoPanel: (() => void) | undefined;
  let previewMounted = false;

  const notifyEquipment = () => {
    const bonus = equipmentStatBonus(equipment);
    saveEquipmentState(options.userId, equipment);
    options.onEquipmentChange(equipment, bonus);
    window.dispatchEvent(
      new CustomEvent(HomeEvents.EquipmentChanged, { detail: { bonus } }),
    );
  };

  const renderSlots = () => {
    const host = shell.querySelector<HTMLElement>("#char-sheet-slots");
    if (!host) return;

    const cells = EQUIPMENT_SLOT_ORDER
      .map((slot) => {
        const itemId = equipment.equipped[slot];
        const item = itemId ? EQUIPMENT_ITEMS[itemId] : undefined;
        const selected = selectedSlot === slot;
        const inner = item
          ? `<span class="char-equip-slot-icon" aria-hidden="true">${item.icon}</span>
             <span class="char-equip-slot-name">${escapeHtml(item.nameTh)}</span>`
          : `<span class="char-equip-slot-empty">ว่าง</span>`;
        return `
          <button type="button"
            class="char-equip-slot${selected ? " char-equip-slot--selected" : ""}${item ? " char-equip-slot--filled" : ""}"
            data-equip-slot="${slot}"
            aria-label="${EQUIPMENT_SLOT_LABEL_TH[slot]}${item ? ` · ${item.nameTh}` : ""}"
            aria-pressed="${selected ? "true" : "false"}">
            <span class="char-equip-slot-label">${EQUIPMENT_SLOT_LABEL_TH[slot]}</span>
            <span class="char-equip-slot-body">${inner}</span>
          </button>`;
      })
      .join("");

    host.innerHTML = cells;
  };

  const renderPicker = () => {
    const host = shell.querySelector<HTMLElement>("#char-sheet-picker");
    if (!host) return;
    if (!selectedSlot) {
      host.hidden = true;
      host.innerHTML = "";
      return;
    }
    const items = inventoryForSlot(equipment, selectedSlot);
    const equippedId = equipment.equipped[selectedSlot];
    const rows =
      items.length > 0
        ? items
            .map((item) => {
              const active = equippedId === item.id;
              return `
              <button type="button" class="char-equip-pick${active ? " char-equip-pick--active" : ""}" data-equip-item="${item.id}">
                <span aria-hidden="true">${item.icon}</span>
                <span>${escapeHtml(item.nameTh)}</span>
              </button>`;
            })
            .join("")
        : `<p class="hint">ยังไม่มีไอเทมสำหรับช่องนี้</p>`;

    const unequipBtn = equippedId
      ? `<button type="button" class="btn btn-ghost btn-xs char-equip-unequip" data-unequip-slot="${selectedSlot}">ถอดออก</button>`
      : "";

    host.hidden = false;
    host.innerHTML = `
      <p class="char-equip-picker-title">เลือก${EQUIPMENT_SLOT_LABEL_TH[selectedSlot]}</p>
      <div class="char-equip-picker-row">${rows}</div>
      ${unequipBtn}
    `;
  };

  const renderEquipMeta = () => {
    const progression = options.getProgression();
    const xp = progressionXpDisplay(progression);
    const ratings = playerRatings(
      progression,
      mergeStatBonuses(equipmentStatBonus(equipment), shadowEquippedStatBonus(options.getEquippedShadow())),
    );
    const nameEl = shell.querySelector<HTMLElement>("#char-sheet-name");
    const lvEl = shell.querySelector<HTMLElement>("#char-sheet-lv");
    const expEl = shell.querySelector<HTMLElement>("#char-sheet-exp");
    const barFill = shell.querySelector<HTMLElement>("#char-sheet-xp-fill");
    const derived = shell.querySelector<HTMLElement>("#char-sheet-derived");

    if (nameEl) nameEl.textContent = options.profile.name;
    if (lvEl) lvEl.textContent = String(progression.level);
    if (expEl) expEl.textContent = `${formatNum(xp.xpIntoLevel)} / ${formatNum(xp.xpToNext || xp.totalExp)}`;
    if (barFill && xp.xpToNext > 0) {
      barFill.style.width = `${Math.min(100, (xp.xpIntoLevel / xp.xpToNext) * 100).toFixed(1)}%`;
    }
    if (derived) {
      derived.textContent = `โจมตี ${ratings.attack} · ป้องกัน ${ratings.defense} · คริ ${(ratings.critChance * 100).toFixed(1)}% ×${ratings.critMultiplier.toFixed(2)}`;
    }
  };

  const syncTabs = () => {
    shell.querySelectorAll<HTMLElement>("[data-char-tab]").forEach((btn) => {
      const tab = btn.dataset.charTab as CharacterSheetTabId;
      const on = tab === activeTab;
      btn.classList.toggle("char-sheet-tab--active", on);
      btn.setAttribute("aria-selected", on ? "true" : "false");
    });
    shell.querySelectorAll<HTMLElement>("[data-char-panel]").forEach((panel) => {
      panel.hidden = panel.dataset.charPanel !== activeTab;
    });
    const statHost = shell.querySelector<HTMLElement>("#char-sheet-stat-host");
    if (activeTab === "stats" && statHost && !stopStatPanel) {
      stopStatPanel = mountStatAllocationPanel(
        statHost,
        options.userId,
        options.getProgression,
        (next) => {
          options.onProgressionUpdate(next);
          renderEquipMeta();
        },
        { getExtraBonus: () => equipmentStatBonus(equipment) },
      );
    }
    const autoHost = shell.querySelector<HTMLElement>("#char-sheet-auto-host");
    if (activeTab !== "auto" && stopAutoPanel) {
      stopAutoPanel();
      stopAutoPanel = undefined;
      if (autoHost) autoHost.innerHTML = "";
    }
    if (activeTab === "auto" && autoHost && !stopAutoPanel) {
      stopAutoPanel = mountItemHotbarSettingsPanel(autoHost, {
        userId: options.userId,
        applyOnButton: true,
        onApplied: (economy) => {
          options.onEconomySettingsApplied?.(economy);
        },
      });
    }
    const shadowHost = shell.querySelector<HTMLElement>("#char-sheet-shadow-host");
    if (activeTab !== "shadow") {
      shadowPanel?.teardown();
      shadowPanel = undefined;
      shadowHost?.replaceChildren();
    } else if (shadowHost && !shadowPanel) {
      shadowPanel = mountShadowPanel(shadowHost, {
        userId: options.userId,
        getEconomy: options.getEconomy,
        onEconomyUpdated: (next) => {
          options.onEconomyUpdated?.(next);
        },
        pendingApCardShadowId: options.getPendingShadowApCardShadowId?.() ?? null,
        onPendingApCardConsumed: options.onPendingShadowApCardConsumed,
      });
    } else if (activeTab === "shadow" && shadowPanel) {
      shadowPanel.repaint();
    }
    const petHost = shell.querySelector<HTMLElement>("#char-sheet-pet-host");
    if (activeTab !== "pet") {
      petPanel?.teardown();
      petPanel = undefined;
      petHost?.replaceChildren();
    } else if (petHost && !petPanel) {
      petPanel = mountPetPanel(petHost, {
        userId: options.userId,
        user: options.user,
        profile: options.profile,
        getEconomy: options.getEconomy,
        onEconomyUpdated: (next) => {
          options.onEconomyUpdated?.(next);
        },
      });
    } else if (activeTab === "pet" && petPanel) {
      petPanel.repaint();
    }
    const spriteHost = shell.querySelector<HTMLElement>("#char-sheet-sprite-host");
    if (activeTab !== "sprite") {
      spritesheetTab?.teardown();
      spritesheetTab = undefined;
      spriteHost?.replaceChildren();
    } else if (spriteHost && !spritesheetTab) {
      spritesheetTab = mountCharacterSpritesheetTab(spriteHost, options.user, options.profile);
    }
  };

  const mountPreviewIfNeeded = () => {
    const previewHost = shell.querySelector<HTMLElement>("#char-sheet-preview-host");
    if (!previewHost || previewMounted) return;
    previewMounted = true;
    stopPreview = mountCharacterIdlePreview(previewHost, options.profile);
  };

  const refresh = () => {
    renderSlots();
    renderPicker();
    renderEquipMeta();
    syncTabs();
    mountPreviewIfNeeded();
  };

  const onShellClick = (event: Event) => {
    const target = event.target as HTMLElement;
    const tabBtn = target.closest<HTMLElement>("[data-char-tab]");
    if (tabBtn?.dataset.charTab) {
      activeTab = tabBtn.dataset.charTab as CharacterSheetTabId;
      syncTabs();
      return;
    }
    if (target.closest("[data-open-skills-modal]")) {
      window.dispatchEvent(new CustomEvent(HomeEvents.OpenSkillsModal));
      return;
    }
    const slotBtn = target.closest<HTMLElement>("[data-equip-slot]");
    if (slotBtn?.dataset.equipSlot) {
      const slot = slotBtn.dataset.equipSlot as EquipmentSlotId;
      selectedSlot = selectedSlot === slot ? null : slot;
      renderSlots();
      renderPicker();
      return;
    }
    const itemBtn = target.closest<HTMLElement>("[data-equip-item]");
    if (itemBtn?.dataset.equipItem) {
      equipment = equipItem(equipment, itemBtn.dataset.equipItem);
      notifyEquipment();
      refresh();
      return;
    }
    const unequipBtn = target.closest<HTMLElement>("[data-unequip-slot]");
    if (unequipBtn?.dataset.unequipSlot) {
      equipment = unequipSlot(equipment, unequipBtn.dataset.unequipSlot as EquipmentSlotId);
      notifyEquipment();
      refresh();
      return;
    }
  };

  shell.addEventListener("click", onShellClick);

  const onProgressionExternal = () => {
    renderEquipMeta();
    if (stopStatPanel) {
      /* stat panel re-renders on its own clicks; external grants need manual refresh */
      const statHost = shell.querySelector<HTMLElement>("#char-sheet-stat-host");
      if (statHost && activeTab === "stats") {
        stopStatPanel();
        stopStatPanel = mountStatAllocationPanel(
          statHost,
          options.userId,
          options.getProgression,
          (next) => {
            options.onProgressionUpdate(next);
            renderEquipMeta();
          },
          { getExtraBonus: () => equipmentStatBonus(equipment) },
        );
      }
    }
  };
  window.addEventListener(HomeEvents.ProgressionChanged, onProgressionExternal);

  refresh();

  return {
    open: () => {
      refresh();
      syncTabs();
    },
    openShadowTab: () => {
      activeTab = "shadow";
      refresh();
      syncTabs();
      shell.hidden = false;
      shell.setAttribute("aria-hidden", "false");
    },
    openSpriteTab: () => {
      activeTab = "sprite";
      refresh();
      syncTabs();
      shell.hidden = false;
      shell.setAttribute("aria-hidden", "false");
    },
    close: () => {
      selectedSlot = null;
      renderPicker();
      stopAutoPanel?.();
      stopAutoPanel = undefined;
      shell.querySelector<HTMLElement>("#char-sheet-auto-host")?.replaceChildren();
      shadowPanel?.teardown();
      shadowPanel = undefined;
      shell.querySelector<HTMLElement>("#char-sheet-shadow-host")?.replaceChildren();
      spritesheetTab?.teardown();
      spritesheetTab = undefined;
      shell.querySelector<HTMLElement>("#char-sheet-sprite-host")?.replaceChildren();
    },
    teardown: () => {
      shell.removeEventListener("click", onShellClick);
      window.removeEventListener(HomeEvents.ProgressionChanged, onProgressionExternal);
      stopPreview?.();
      stopStatPanel?.();
      stopAutoPanel?.();
      shadowPanel?.teardown();
      petPanel?.teardown();
      spritesheetTab?.teardown();
    },
    getEquipmentState: () => equipment,
  };
}

export function characterSheetModalMarkup(): string {
  return `
    <div id="home-character-modal" class="home-character-modal" hidden aria-hidden="true">
      <button type="button" class="home-character-backdrop" data-close-character-sheet aria-label="ปิด"></button>
      <div class="home-character-dialog home-character-dialog--game" role="dialog" aria-modal="true" aria-labelledby="home-character-title">
        <header class="home-character-head home-character-head--game">
          <div class="home-character-head-text">
            <p class="dungeon-frame-kicker">ระบบ</p>
            <h2 id="home-character-title" class="dungeon-frame-title home-character-title">ตัวละคร</h2>
          </div>
          <button type="button" class="dungeon-frame-close" data-close-character-sheet aria-label="ปิด">×</button>
        </header>
        <div class="char-sheet-tabs char-sheet-tabs--game" role="tablist" aria-label="แท็บตัวละคร">
          <button type="button" class="char-sheet-tab char-sheet-tab--active" data-char-tab="equip" role="tab" aria-selected="true">สวมใส่</button>
          <button type="button" class="char-sheet-tab" data-char-tab="stats" role="tab" aria-selected="false">สเตต</button>
          <button type="button" class="char-sheet-tab" data-char-tab="auto" role="tab" aria-selected="false">กำหนดค่า auto</button>
          <button type="button" class="char-sheet-tab" data-open-skills-modal>สกิล</button>
          <button type="button" class="char-sheet-tab" data-char-tab="shadow" role="tab" aria-selected="false">ร่างเงา</button>
          <button type="button" class="char-sheet-tab" data-char-tab="pet" role="tab" aria-selected="false">สัตว์เลี้ยง</button>
          <button type="button" class="char-sheet-tab" data-char-tab="sprite" role="tab" aria-selected="false">ตัวละคร</button>
        </div>
        <div class="home-character-body">
          <section class="char-sheet-panel" data-char-panel="equip">
            <div class="char-sheet-equip-layout">
              <div class="char-sheet-hero">
                <div id="char-sheet-preview-host" class="char-sheet-preview-host"></div>
                <p class="char-sheet-name" id="char-sheet-name">—</p>
                <p class="char-sheet-lv-row">
                  <span class="char-sheet-lv-badge">Lv <span id="char-sheet-lv">1</span></span>
                  <span class="hint" id="char-sheet-exp">0 / 0</span>
                </p>
                <div class="stat-xp-bar char-sheet-xp-bar" role="progressbar">
                  <div class="stat-xp-fill" id="char-sheet-xp-fill" style="width:0%"></div>
                </div>
                <p class="hint char-sheet-derived" id="char-sheet-derived"></p>
              </div>
              <div class="char-sheet-equip-col">
                <div id="char-sheet-slots" class="char-sheet-slots" aria-label="ช่องสวมใส่"></div>
                <div id="char-sheet-picker" class="char-sheet-picker" hidden></div>
              </div>
            </div>
          </section>
          <section class="char-sheet-panel" data-char-panel="stats" hidden>
            <div id="char-sheet-stat-host"></div>
          </section>
          <section class="char-sheet-panel" data-char-panel="auto" hidden>
            <div id="char-sheet-auto-host"></div>
          </section>
          <section class="char-sheet-panel" data-char-panel="shadow" hidden>
            <div id="char-sheet-shadow-host"></div>
          </section>
          <section class="char-sheet-panel" data-char-panel="pet" hidden>
            <div id="char-sheet-pet-host"></div>
          </section>
          <section class="char-sheet-panel" data-char-panel="sprite" hidden>
            <div id="char-sheet-sprite-host"></div>
          </section>
        </div>
      </div>
    </div>
  `;
}
